import axios from 'axios';
import config from '../config';
import { MESSAGES } from '../constants/messages';
import { getToken, clearAuthData } from './auth';
import { buildLLMConfig } from '../constants/llmConfig';
import { DEFAULT_MODEL } from '../constants/models';

const apiClient = axios.create({
  baseURL: config.api.baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: config.api.timeout,
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableError = (error) => {
  if (!error.response) return true;
  const status = error.response.status;
  return status === 408 || status === 429 || (status >= 500 && status < 600);
};

apiClient.interceptors.request.use(
  (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };
    
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      clearAuthData();
      window.location.reload();
      return Promise.reject(error);
    }

    if (!originalRequest._retry && isRetryableError(error)) {
      originalRequest._retryCount = originalRequest._retryCount || 0;

      if (originalRequest._retryCount < config.api.retryAttempts) {
        originalRequest._retryCount++;
        const delayMs = config.api.retryDelay * originalRequest._retryCount;
        await sleep(delayMs);
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

const parseApiError = (error) => {
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return MESSAGES.ERROR.TIMEOUT_ERROR;
  }

  if (!error.response) {
    return MESSAGES.ERROR.NETWORK_ERROR;
  }

  const status = error.response.status;
  const data = error.response.data;

  if (data?.detail) {
    return typeof data.detail === 'string' ? data.detail : MESSAGES.ERROR.UNKNOWN_ERROR;
  }

  if (status === 401) return 'Please login to continue';
  if (status === 403) return 'You do not have permission';
  if (status === 404) return 'Resource not found';
  if (status === 429) return 'Too many requests. Please try again later.';
  if (status >= 500) return 'Server error. Please try again.';

  return MESSAGES.ERROR.UNKNOWN_ERROR;
};

export const generatePrompts = async (projectName, projectId, payload, onProjectId, onChunk, onComplete, onError, workspaceId = null) => {
  const token = getToken();
  
  try {
    const llmConfig = payload.llm_config || buildLLMConfig(payload.model || DEFAULT_MODEL);
    const requestBody = {
      project_name: projectName,
      project_id: projectId,
      user_input: payload.user_input,
      llm_config: llmConfig,
    };
    if (workspaceId) {
      requestBody.workspace_id = workspaceId;
    }
    
    const response = await fetch(`${config.api.baseURL}/api/v1/generate-prompt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'project_id') {
              onProjectId(data.id);
            } else if (data.type === 'start') {
              onChunk({ type: 'start', label: data.label });
            } else if (data.type === 'chunk') {
              onChunk({ type: 'chunk', label: data.label, content: data.content });
            } else if (data.type === 'done') {
              onComplete(data.id, data.additional_variables);
            }
          } catch (e) {
          }
        }
      }
    }

    return { success: true };
  } catch (error) {
    if (onError) {
      onError(error.message);
    }
    return { success: false, error: error.message };
  }
};

export const getProjects = async (limit = 50, skip = 0, workspaceId = null) => {
  try {
    const response = await apiClient.post('/api/v1/all-generations', {
      limit,
      skip,
      workspace_id: workspaceId,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getProject = async (projectId) => {
  if (!projectId) {
    return { success: false, error: 'Project ID is required' };
  }
  try {
    const response = await apiClient.post('/api/v1/generations', {
      generation_id: projectId,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const deleteProject = async (projectId) => {
  if (!projectId) {
    return { success: false, error: 'Project ID is required' };
  }
  try {
    await apiClient.post('/api/v1/generations/delete', {
      generation_id: projectId,
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const enhanceDescription = async (description, workspaceId, projectId = null, projectName = null) => {
  try {
    const requestBody = {
      description,
      workspace_id: workspaceId,
    };
    
    if (projectId) {
      requestBody.project_id = projectId;
    }
    
    if (projectName) {
      requestBody.project_name = projectName;
    }
    
    const response = await apiClient.post('/api/v1/enhancement/description', requestBody);
    return { success: true, data: response.data.enhanced_text };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const enhanceUseCase = async (useCase, description = null, workspaceId, projectId = null, projectName = null) => {
  try {
    const requestBody = {
      use_case: useCase,
      description: description,
      workspace_id: workspaceId,
    };
    
    if (projectId) {
      requestBody.project_id = projectId;
    }
    
    if (projectName) {
      requestBody.project_name = projectName;
    }
    
    const response = await apiClient.post('/api/v1/enhancement/use-case', requestBody);
    return { success: true, data: response.data.enhanced_text };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const checkApiHealth = async () => {
  try {
    const response = await apiClient.get('/health');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updatePassword = async (currentPassword, newPassword) => {
  try {
    const response = await apiClient.post('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const updateProfile = async (email, fullName) => {
  try {
    const response = await apiClient.post('/api/v1/auth/profile', {
      email: email || undefined,
      full_name: fullName || undefined
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const runPrompt = async (projectId, runName, inputValues, llmConfigOrModel, onChunk, onComplete, onError, workspaceId = null) => {
  const token = getToken();
  
  try {
    let llmConfig;
    if (typeof llmConfigOrModel === 'string') {
      llmConfig = buildLLMConfig(llmConfigOrModel);
    } else if (llmConfigOrModel && typeof llmConfigOrModel === 'object') {
      llmConfig = llmConfigOrModel.model ? llmConfigOrModel : buildLLMConfig(DEFAULT_MODEL);
    } else {
      llmConfig = buildLLMConfig(DEFAULT_MODEL);
    }

    const requestBody = {
      project_id: projectId,
      run_name: runName,
      input_values: inputValues,
      llm_config: llmConfig,
    };
    if (workspaceId) {
      requestBody.workspace_id = workspaceId;
    }

    const response = await fetch(`${config.api.baseURL}/api/v1/run-prompt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'chunk') {
              onChunk(data.content);
            } else if (data.type === 'done') {
              onComplete(data);
            } else if (data.type === 'error') {
              if (onError) onError(data.message);
            }
          } catch (e) {
          }
        }
      }
    }

    return { success: true };
  } catch (error) {
    if (onError) onError(error.message);
    return { success: false, error: error.message };
  }
};

export const getAllRuns = async (limit = 50, skip = 0, workspaceId = null) => {
  try {
    const response = await apiClient.post('/api/v1/prompt-runs', {
      limit,
      skip,
      workspace_id: workspaceId,
    });
    return { success: true, data: response.data.runs };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getProjectRuns = async (projectId, limit = 50) => {
  try {
    const response = await apiClient.post('/api/v1/prompt-runs/project', {
      project_id: projectId,
      limit,
    });
    return { success: true, data: response.data.runs };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getRunById = async (runId) => {
  try {
    const response = await apiClient.post('/api/v1/prompt-runs/run', {
      run_id: runId,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteRun = async (runId) => {
  try {
    await apiClient.post('/api/v1/prompt-runs/delete', {
      run_id: runId,
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const refinePrompt = async (projectId, feedback, model, llmConfig, draftCommitId, onStageChange, onRefinementChunk, onAnalysisChunk, onPreviewReady, onError) => {
  const token = getToken();
  
  try {
    const requestBody = {
      project_id: projectId,
      feedback: feedback,
      model: model || DEFAULT_MODEL,
      llm_config: llmConfig || {},
    };
    if (draftCommitId) {
      requestBody.draft_commit_id = draftCommitId;
    }
    
    const response = await fetch(`${config.api.baseURL}/api/v1/refine-prompt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'start') {
              if (onStageChange) onStageChange(data.stage);
            } else if (data.type === 'analysis_complete') {
              if (onStageChange) onStageChange('analysis_complete', data);
            } else if (data.type === 'refinement_chunk') {
              if (onRefinementChunk) onRefinementChunk(data.content, data.prompt_type);
            } else if (data.type === 'analysis_chunk') {
              if (onAnalysisChunk) onAnalysisChunk(data.content);
            } else if (data.type === 'preview_ready') {
              if (onPreviewReady) onPreviewReady(data);
            } else if (data.type === 'no_changes') {
              if (onError) onError(data.reasoning || 'No changes needed');
            } else if (data.type === 'error') {
              if (onError) onError(data.message);
            } else if (data.type === 'warning' || data.type === 'info') {
            }
          } catch (e) {
          }
        }
      }
    }

    return { success: true };
  } catch (error) {
    if (onError) onError(error.message);
    return { success: false, error: error.message };
  }
};

export const saveRefinement = async (refinementData) => {
  try {
    const response = await apiClient.post('/api/v1/save-refinement', refinementData);
    return { success: true, data: response.data };
  } catch (error) {
    return { 
      success: false, 
      error: parseApiError(error),
      isConflict: error.response?.status === 409
    };
  }
};

export const createDraft = async (promptId, baseCommitId = null) => {
  try {
    const response = await apiClient.post('/api/v1/create-draft', { 
      prompt_id: promptId,
      base_commit_id: baseCommitId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { 
      success: false, 
      error: parseApiError(error),
      isConflict: error.response?.status === 409
    };
  }
};

export const publishDraft = async (promptId, draftCommitId, customCommitMessage = null, generateAI = false) => {
  try {
    const response = await apiClient.post('/api/v1/publish-draft', {
      prompt_id: promptId,
      draft_commit_id: draftCommitId,
      custom_commit_message: customCommitMessage,
      generate_ai_message: generateAI
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { 
      success: false, 
      error: parseApiError(error),
      isConflict: error.response?.status === 409
    };
  }
};

export const discardDraft = async (promptId, draftCommitId) => {
  try {
    const response = await apiClient.post('/api/v1/discard-draft', {
      prompt_id: promptId,
      draft_commit_id: draftCommitId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const updateDraft = async (promptId, draftCommitId, systemPrompt = null, userPrompt = null, fewShotExamples = null) => {
  try {
    const requestBody = {
      prompt_id: promptId,
      draft_commit_id: draftCommitId
    };
    if (systemPrompt !== null) {
      requestBody.system_prompt = systemPrompt;
    }
    if (userPrompt !== null) {
      requestBody.user_prompt = userPrompt;
    }
    if (fewShotExamples !== null) {
      requestBody.few_shot_examples = fewShotExamples;
    }
    
    const response = await apiClient.post('/api/v1/update-draft', requestBody);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getVersionHistory = async (projectId, limit = 50, includeDrafts = true) => {
  if (!projectId) {
    return { success: false, error: 'Project ID is required' };
  }
  try {
    const response = await apiClient.post('/api/v1/versions/history', {
      project_id: projectId,
      limit: limit,
      include_drafts: includeDrafts
    });
    return { success: true, data: response.data.versions };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getVersionByCommit = async (commitId) => {
  if (!commitId) {
    return { success: false, error: 'Commit ID is required' };
  }
  try {
    const response = await apiClient.post('/api/v1/versions/by-commit', {
      commit_id: commitId,
    });
    return { success: true, data: response.data.version };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const restoreVersion = async (projectId, commitId, createBranch = false, branchName = null) => {
  if (!projectId || !commitId) {
    return { success: false, error: 'Project ID and Commit ID are required' };
  }
  try {
    const response = await apiClient.post('/api/v1/versions/restore', {
      project_id: projectId,
      commit_id: commitId,
      create_branch: createBranch,
      branch_name: branchName
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const compareVersions = async (commitId1, commitId2) => {
  if (!commitId1 || !commitId2) {
    return { success: false, error: 'Both commit IDs are required' };
  }
  try {
    const response = await apiClient.post('/api/v1/versions/compare', {
      commit_id_1: commitId1,
      commit_id_2: commitId2,
    });
    return { success: true, data: response.data.comparison };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getVersionTree = async (projectId) => {
  if (!projectId) {
    return { success: false, error: 'Project ID is required' };
  }
  try {
    const response = await apiClient.post('/api/v1/versions/tree', {
      project_id: projectId,
    });
    return { success: true, data: response.data.tree };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getWorkspaces = async () => {
  try {
    const response = await apiClient.get('/api/v1/workspaces');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const createWorkspace = async (name, slug = null) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces', { name, slug });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getWorkspace = async (workspaceId) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/get', {
      workspace_id: workspaceId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const updateWorkspace = async (workspaceId, name) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/update', {
      workspace_id: workspaceId,
      name
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const deleteWorkspace = async (workspaceId) => {
  try {
    await apiClient.post('/api/v1/workspaces/delete', {
      workspace_id: workspaceId
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getWorkspaceStats = async (workspaceId) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/stats', {
      workspace_id: workspaceId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getWorkspaceMembers = async (workspaceId) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/members/list', {
      workspace_id: workspaceId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const addWorkspaceMember = async (workspaceId, shareId, role = 'member', editProjectIds = [], viewProjectIds = []) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/members/add', {
      workspace_id: workspaceId,
      share_id: shareId,
      role,
      edit_project_ids: editProjectIds,
      view_project_ids: viewProjectIds
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const removeWorkspaceMember = async (workspaceId, userId) => {
  try {
    await apiClient.post('/api/v1/workspaces/members/remove', {
      workspace_id: workspaceId,
      user_id: userId
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const updateMemberRole = async (workspaceId, userId, role) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/members/update-role', {
      workspace_id: workspaceId,
      user_id: userId,
      role
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

// API Key Management
export const getWorkspaceAPIKeys = async (workspaceId) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/api-keys/get', {
      workspace_id: workspaceId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const setWorkspaceAPIKeys = async (workspaceId, keys) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/api-keys/set', {
      workspace_id: workspaceId,
      openai_api_key: keys.openai_api_key,
      anthropic_api_key: keys.anthropic_api_key,
      groq_api_key: keys.groq_api_key
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const deleteWorkspaceAPIKey = async (workspaceId, provider) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/api-keys/delete', {
      workspace_id: workspaceId,
      provider
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getProjectCollaborators = async (projectId) => {
  try {
    const response = await apiClient.post('/api/v1/projects/collaborators', {
      project_id: projectId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const updateProjectPermission = async (workspaceId, projectId, userId, permission) => {
  try {
    const response = await apiClient.post('/api/v1/workspaces/projects/update-permission', {
      workspace_id: workspaceId,
      project_id: projectId,
      user_id: userId,
      permission
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getMonitoringLiveStats = async (workspaceId, hours = 1) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/live-stats', {
      workspace_id: workspaceId,
      hours
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getMonitoringRecentRuns = async (workspaceId, limit = 100) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/recent-runs', {
      workspace_id: workspaceId,
      limit
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getMonitoringGenerationRuns = async (workspaceId, limit = 100) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/generation-runs', {
      workspace_id: workspaceId,
      limit
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getMonitoringRefinementRuns = async (workspaceId, limit = 100) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/refinement-runs', {
      workspace_id: workspaceId,
      limit
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getMonitoringAdditionalRuns = async (workspaceId, limit = 100) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/additional-runs', {
      workspace_id: workspaceId,
      limit
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getMonitoringCombinedRuns = async (workspaceId, limit = 100) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/combined-runs', {
      workspace_id: workspaceId,
      limit
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const checkMonitoringHealth = async () => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/health');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getModelComparisonStats = async (workspaceId, hours = 24, projectId = null) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/model-comparison-stats', {
      workspace_id: workspaceId,
      hours,
      project_id: projectId
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getModelComparisonBreakdown = async (workspaceId, hours = 24) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/model-comparison-breakdown', {
      workspace_id: workspaceId,
      hours
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getRecentModelComparisons = async (workspaceId, limit = 50) => {
  try {
    const response = await apiClient.post('/api/v1/monitoring/recent-model-comparisons', {
      workspace_id: workspaceId,
      limit
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const createManualProject = async (projectData) => {
  try {
    const response = await apiClient.post('/api/v1/manual-prompt/create', projectData);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const extractVariables = async (prompts) => {
  try {
    const response = await apiClient.post('/api/v1/manual-prompt/extract-variables', prompts);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const exportPromptAsSDK = async (projectId, sdkType) => {
  const token = getToken();
  
  try {
    const response = await fetch(`${config.api.baseURL}/api/v1/export-prompt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        project_id: projectId,
        sdk_type: sdkType
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Export failed: ${response.status}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    
    // Parse filename from Content-Disposition header or create appropriate fallback
    let filename;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      filename = filenameMatch ? filenameMatch[1] : null;
    }
    
    // Fallback filename based on SDK type
    if (!filename) {
      const extensions = {
        'curl': 'sh',
        'markdown': 'md',
        'langchain': 'py',
        'openai': 'py',
        'anthropic': 'py',
        'litellm': 'py'
      };
      const ext = extensions[sdkType] || 'py';
      filename = `prompt_${sdkType}.${ext}`;
    }
    
    return { success: true, blob, filename };
  } catch (error) {
    return { success: false, error: error.message || 'Failed to export prompt' };
  }
};


// SDK API Key Management
export const createSDKApiKey = async (workspaceId, name, permissions = ['read']) => {
  try {
    const user = JSON.parse(localStorage.getItem('promptforge_user') || '{}');
    const response = await apiClient.post('/api/v1/sdk/create_api_key', {
      workspace_id: workspaceId,
      name,
      permissions
    }, {
      headers: { 'X-User-ID': user.user_id || user.id }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const listSDKApiKeys = async (workspaceId) => {
  try {
    const user = JSON.parse(localStorage.getItem('promptforge_user') || '{}');
    const response = await apiClient.post('/api/v1/sdk/list_api_keys', {
      workspace_id: workspaceId
    }, {
      headers: { 'X-User-ID': user.user_id || user.id }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const revokeSDKApiKey = async (workspaceId, keyId) => {
  try {
    const user = JSON.parse(localStorage.getItem('promptforge_user') || '{}');
    const response = await apiClient.post('/api/v1/sdk/revoke_api_key', {
      workspace_id: workspaceId,
      key_id: keyId
    }, {
      headers: { 'X-User-ID': user.user_id || user.id }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getSDKApiLogs = async (workspaceId, options = {}) => {
  try {
    const user = JSON.parse(localStorage.getItem('promptforge_user') || '{}');
    const response = await apiClient.post('/api/v1/sdk/get_api_logs', {
      workspace_id: workspaceId,
      limit: options.limit || 100,
      offset: options.offset || 0,
      key_id: options.keyId || null,
      endpoint: options.endpoint || null,
      status_code: options.statusCode || null
    }, {
      headers: { 'X-User-ID': user.user_id || user.id }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export const getSDKApiStats = async (workspaceId, days = 30) => {
  try {
    const user = JSON.parse(localStorage.getItem('promptforge_user') || '{}');
    const response = await apiClient.post('/api/v1/sdk/get_api_stats', {
      workspace_id: workspaceId,
      days
    }, {
      headers: { 'X-User-ID': user.user_id || user.id }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: parseApiError(error) };
  }
};

export default apiClient;
